import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import {
  OrbitOneNavIcon,
  useGetRouteTreeGroups,
} from '@/components/layoutAndProvidersWrapper/sitecontrolLayout/baseLayout';
import { DeadFishIcon } from '@/icons/DeadFishIcon.tsx';
import { menuIonStyle } from '@/pages/layouts/getStartedModal/gettingStarted_KeyboardShortcuts/types.ts';
import { routeTree } from '@/routeTree.gen.ts';
import { Icon } from '@/utils';

import type { Hotkey } from '../hotkeys';
import { Key } from '../hotkeys/KeyStrokes.ts';
import { GlobalHotkeyName } from '../hotkeys/types.ts';

/** Widen to `Record<string, Hotkey>` so optional fields like `hotkeyModifier` exist on every value type. */
export const useGlobalHotkeysDefinitions = (): Record<string, Hotkey>[] => {
  const { t } = useTranslation(['common']);
  const { services, utils } = useGetRouteTreeGroups(routeTree);
  const availableServices = useMemo(() => services.map(s => s.path.toLowerCase()), [services]);
  const availableUtils = useMemo(() => utils.map(s => s.path.toLowerCase()), [utils]);
  return useMemo((): Record<string, Hotkey>[] => {
    return [
      {
        [GlobalHotkeyName.DashboardPageKey]: {
          primary: Key.navigation.Backspace,
          description: t('common:pages.dashboard'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
          menuIconForAction: <Icon.LayoutDashboard style={menuIonStyle} />,
        },
        [GlobalHotkeyName.LayoutsPageKey]: {
          primary: Key.KeyL,
          description: t('common:pages.layouts'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
          menuIconForAction: <Icon.Grid3x3 style={menuIonStyle} />,
        },
      },
      {
        ...(availableServices.includes('bargecontrol'.toLowerCase()) && {
          [GlobalHotkeyName.BargeControlsPageKey]: {
            primary: Key.KeyB,
            description: t('common:pages.bargeControl'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.Barge style={menuIonStyle} />,
          },
        }),
        ...(availableServices.includes('hybrid-power'.toLowerCase()) && {
          [GlobalHotkeyName.HybridPowerPageKey]: {
            primary: Key.KeyH,
            description: t('common:pages.hybridPower'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.BatteryFull style={menuIonStyle} />,
          },
        }),
        ...(availableServices.includes('orbitOne'.toLowerCase()) && {
          [GlobalHotkeyName.OrbitOnePageKey]: {
            primary: Key.KeyO,
            description: t('common:pages.orbitOne'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <OrbitOneNavIcon />,
          },
        }),
        ...(availableServices.includes('penViewer'.toLowerCase()) && {
          [GlobalHotkeyName.PenViewerPageKey]: {
            primary: Key.KeyP,
            description: t('common:pages.penViewer'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.Unit style={menuIonStyle} />,
          },
        }),
        ...(availableUtils.includes('automation'.toLowerCase()) && {
          [GlobalHotkeyName.AutomationPageKey]: {
            primary: Key.KeyA,
            description: t('common:pages.automation'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.WandSparkles style={menuIonStyle} />,
          },
        }),
        ...(availableServices.includes('upcycle'.toLowerCase()) && {
          [GlobalHotkeyName.UpCyclePageKey]: {
            primary: Key.KeyU,
            description: t('common:pages.upCycle'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <DeadFishIcon style={menuIonStyle} />,
          },
        }),
      },
      {
        // Camera Control is the Orbit One camera page; it is offered when that service is on the site.
        ...(availableServices.includes('orbitOne'.toLowerCase()) && {
          [GlobalHotkeyName.CameraControlsPageKey]: {
            primary: Key.KeyC,
            description: t('common:pages.cameraCheck'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.MonitorPlay style={menuIonStyle} />,
          },
        }),
        ...(availableUtils.includes('deviceconfig'.toLowerCase()) && {
          [GlobalHotkeyName.DeviceConfigurationPageKey]: {
            primary: Key.KeyD,
            description: t('common:pages.deviceConfig'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.Settings2 style={menuIonStyle} />,
          },
        }),
        ...(availableUtils.includes('eventstream'.toLowerCase()) && {
          [GlobalHotkeyName.EventStreamPageKey]: {
            primary: Key.KeyE,
            description: t('common:pages.eventStream'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.WavesHorizontal style={menuIonStyle} />,
          },
        }),
        ...(availableUtils.includes('inventory'.toLowerCase()) && {
          [GlobalHotkeyName.InventoryPageKey]: {
            primary: Key.KeyI,
            description: t('common:pages.inventory'),
            hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
            menuIconForAction: <Icon.Package style={menuIonStyle} />,
          },
        }),
        [GlobalHotkeyName.SettingsPageKey]: {
          primary: Key.symbol.Comma,
          description: t('common:pages.settings'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
          menuIconForAction: <Icon.Settings style={menuIonStyle} />,
        },
      },
      {
        [GlobalHotkeyName.ShowKeyboardShortcutsModalKey]: {
          primary: Key.KeyS,
          description: t('common:shortcuts.globalShortcuts.showKeyboardShortcutsModal'),
          hotkeyModifier: { isModifierIconLast: true, modifier: 'Alt' },
        },
      },
    ];
  }, [t, availableServices, availableUtils]);
};
